import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Switch } from '@/components/ui/Switch';
import { type AppWithExclusionState } from '@/features/electron-api';
import { cn } from '@/lib/utils';

import { appRowPresentation } from './app-access-mode';
import { AppAccessSwitchCoachMark } from './AppAccessSwitchCoachMark';
import { lockedByLabel, useAppExclusionsContext } from './use-app-exclusions';

type FilterType = 'all' | 'excluded' | 'included';

export const AppExclusionList = ({
  showSwitchCoachMark = false,
}: {
  showSwitchCoachMark?: boolean;
}) => {
  const {
    apps,
    savingAppAccessMode,
    loading,
    loadFailed,
    error,
    reloadApps,
    toggleApp,
    addAppManually,
    addingAppManually,
  } = useAppExclusionsContext();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const stickyVisibleAppsRef = useRef<Set<string>>(new Set());

  const filteredAppsByType = useMemo(() => {
    let filtered: AppWithExclusionState[];
    if (filterType === 'excluded') {
      filtered = apps.filter((app) => app.isExcluded);
    } else if (filterType === 'included') {
      filtered = apps.filter((app) => !app.isExcluded);
    } else {
      filtered = apps;
    }

    const visibleIds = new Set([
      ...filtered.map((app) => app.identifier),
      ...stickyVisibleAppsRef.current,
    ]);
    return apps.filter((app) => visibleIds.has(app.identifier));
  }, [filterType, apps]);

  const filteredApps = useMemo(() => {
    if (searchQuery === '') return filteredAppsByType;
    return filteredAppsByType.filter((app) =>
      app.displayName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [filteredAppsByType, searchQuery]);

  const handleToggle = useCallback(
    (identifier: string) => {
      toggleApp(identifier);
      stickyVisibleAppsRef.current.add(identifier);
    },
    [toggleApp]
  );

  const includedCount = apps.filter((app) => !app.isExcluded).length;
  const includedCountLabel = (
    <span className="shrink-0 tabular-nums" role="status">
      {includedCount} {includedCount === 1 ? 'app' : 'apps'} included
    </span>
  );

  return (
    <YStack className="gap-4 w-full">
      {!loading && !loadFailed && (
        <XStack className="items-center justify-between gap-3 text-sm text-foreground-muted">
          <span>Turn on apps Littlebird can read.</span>
          {showSwitchCoachMark ? (
            <AppAccessSwitchCoachMark returnFocusRef={searchInputRef}>
              {includedCountLabel}
            </AppAccessSwitchCoachMark>
          ) : (
            includedCountLabel
          )}
        </XStack>
      )}
      <XStack className="flex-wrap gap-3 items-center">
        <XStack className="min-w-0 basis-48 items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 flex-1 focus-within:ring-2 focus-within:ring-ring">
          <Icons.search className="size-3.5 text-foreground-muted shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            aria-label="Search applications"
            placeholder="Search applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-transparent text-sm outline-none w-full placeholder:text-foreground-muted"
          />
          {searchQuery && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
              className="shrink-0"
            >
              <Icons.cancel className="size-3 text-foreground-muted hover:text-foreground" />
            </button>
          )}
        </XStack>
        <SegmentedControl.Root
          value={filterType}
          onValueChange={(value) => {
            setFilterType(value as FilterType);
            stickyVisibleAppsRef.current.clear();
          }}
        >
          <SegmentedControl.List>
            <SegmentedControl.Trigger value="all">All</SegmentedControl.Trigger>
            <SegmentedControl.Trigger value="included">
              Included
            </SegmentedControl.Trigger>
            <SegmentedControl.Trigger value="excluded">
              Excluded
            </SegmentedControl.Trigger>
          </SegmentedControl.List>
        </SegmentedControl.Root>
      </XStack>
      <ScrollArea className="h-[50vh] max-h-[440px] bg-surface border border-border rounded-lg py-2 px-2">
        {loading ? (
          <div className="text-xs text-foreground-muted p-2">
            Loading applications...
          </div>
        ) : loadFailed && apps.length === 0 ? (
          <div className="text-xs text-foreground-muted p-2">
            Couldn't load your applications.{' '}
            <button
              type="button"
              onClick={reloadApps}
              className="underline underline-offset-4 text-foreground"
            >
              Try again
            </button>
          </div>
        ) : filteredApps.length === 0 ? (
          <YStack className="items-start gap-2 text-sm text-foreground-muted p-2">
            {searchQuery
              ? `No apps match “${searchQuery}”.`
              : filterType === 'included'
                ? 'No apps included yet.'
                : filterType === 'excluded'
                  ? 'No apps excluded.'
                  : 'No applications found.'}
            {(searchQuery || filterType !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                  stickyVisibleAppsRef.current.clear();
                }}
              >
                Show all apps
              </Button>
            )}
          </YStack>
        ) : (
          <YStack className="gap-1">
            {filteredApps.map((app) => (
              <AppExclusionItem
                key={app.identifier}
                app={app}
                disabled={savingAppAccessMode}
                onToggle={handleToggle}
              />
            ))}
          </YStack>
        )}
      </ScrollArea>
      <XStack className="justify-between py-2 px-1">
        <span className="text-sm text-foreground-subtle">
          Can't find your app?{' '}
          <button
            type="button"
            onClick={addAppManually}
            disabled={addingAppManually}
            className="underline underline-offset-4 text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            Select it manually
          </button>
        </span>
        <p className="text-danger-6 text-xs">{error}</p>
      </XStack>
    </YStack>
  );
};

type AppExclusionItemProps = {
  app: AppWithExclusionState;
  disabled?: boolean;
  onToggle: (identifier: string) => void;
};

export const AppExclusionItem = memo(
  ({ app, disabled = false, onToggle }: AppExclusionItemProps) => {
    const { checked, switchLabel, status } = appRowPresentation(app);
    const handleToggle = () => {
      if (app.canUserModify && !disabled) onToggle(app.identifier);
    };

    return (
      <XStack
        className={cn(
          'group items-center gap-3 py-1 px-2 rounded',
          app.canUserModify && 'hover:bg-surface-hover',
          app.isDefaultExclusion && !app.canUserModify && 'bg-surface-muted'
        )}
        onClick={handleToggle}
      >
        <div className="size-8 rounded-lg bg-secondary flex items-center justify-center shrink-0 relative">
          <Icons.imageOff className="size-4 text-foreground-muted absolute" />
          <img src={app.icon} alt="" className="size-8 rounded-lg relative" />
        </div>

        <span
          className="flex-1 text-sm truncate flex items-center"
          style={{ minHeight: '32px' }}
        >
          {app.displayName}
          {app.lockedBy && (
            <span className="ml-1 text-foreground-muted inline-block">
              {lockedByLabel(app.lockedBy)}
            </span>
          )}
        </span>

        <span className="text-xs text-foreground-muted">{status}</span>

        {app.canUserModify ? (
          <Switch
            checked={checked}
            disabled={disabled}
            aria-label={switchLabel}
          />
        ) : (
          <XStack className="w-8 shrink-0 justify-center text-foreground-muted">
            <Icons.lock className="size-3.5" aria-hidden="true" />
          </XStack>
        )}
      </XStack>
    );
  }
);
